import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { not } from 'tio-ui/utilities';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type { NotificationsService } from 'tio-reimagined/components/notifications';
import type TasProgramInstanceModel from '../../../models/tas-program-instance.ts';
import { doEmployeesExistInApproverList } from '../../../utils/tuition-assistance/approvers.ts';
import {
  getSingleValueForTasField,
  getSortedOptionsByFieldName,
  type TemplateFieldName,
} from '../../../utils/tuition-assistance/fields.ts';
import Button from 'tio-reimagined/components/button';
import Popover from 'tio-reimagined/components/popover';
import { Listbox } from 'tio-ui/components/collections';
import Modal from 'tio-reimagined/components/modal';
import Textarea from 'tio-reimagined/components/textarea';

import TooltipButton from 'tio-reimagined/components/tooltip-button';
import ConfirmationDialog from 'tio-reimagined/components/confirmation-dialog';
import SelectTextArea from '../../tio/select-text-area.gts';
import {
  PhChatCenteredText,
  PhBuilding,
  PhFile,
  PhCalendarDots,
  PhUsers,
  PhDotsThreeVertical,
  PhArrowsCounterClockwise,
  PhArrowUUpLeft,
  PhXCircle,
} from 'ember-phosphor-icons';
import type { ProgramDrawerType } from './types.ts';

import type SessionContextService from '../../../services/session-context.ts';
interface ProgramApprovalFooterSignature {
  Element: HTMLDivElement;
  Args: {
    instance: TasProgramInstanceModel;
    onDrawerOpen: (drawerType: ProgramDrawerType) => void;
    activeDrawer?: ProgramDrawerType | null;
    consumingApp?: string;
  };
}

export default class ProgramApprovalFooter extends Component<ProgramApprovalFooterSignature> {
  @service declare store: Store;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;

  // Modal states
  @tracked showApproveModal = false;
  @tracked showRejectModal = false;
  @tracked showRequestChangesModal = false;
  @tracked showRescindModal = false;
  @tracked approveNote = '';
  @tracked rejectNote = '';
  @tracked requestChangeNote = '';
  @tracked hasSubmitted = false;
  @tracked submitError: string | null = null;
  @tracked showConfirmApproverOverridePrompt = false;

  get approveCommentErrors(): string[] {
    if (!this.hasSubmitted) return [];
    if (!this.approveNote.trim()) return ['Comment is required'];
    return [];
  }

  get rejectCommentErrors(): string[] {
    if (!this.hasSubmitted) return [];
    if (!this.rejectNote.trim()) return ['Comment is required'];
    return [];
  }

  get requestChangeCommentErrors(): string[] {
    if (!this.hasSubmitted) return [];
    if (!this.requestChangeNote.trim()) return ['Comment is required'];
    return [];
  }

  // ── State Detection ────────────────────────────────────────────────────

  get state() {
    return this.args.instance.state;
  }

  get currentPhase(): 'program' | 'completion' | 'withdrawal' | null {
    if (this.state.includes('PROGRAM_APPROVAL')) return 'program';
    if (this.state.includes('COMPLETION')) return 'completion';
    if (this.state.includes('WITHDRAWAL')) return 'withdrawal';
    return null;
  }

  get currentPhaseLabel() {
    switch (this.currentPhase) {
      case 'completion':
        return 'Completion';
      case 'withdrawal':
        return 'Withdrawal';
      case 'program':
      default:
        return 'Program';
    }
  }

  get inPendingApprovalState() {
    return this.state.includes('PENDING');
  }

  get canApprove() {
    return this.inPendingApprovalState;
  }

  get canRescind() {
    return this.args.instance.inRescindableState;
  }

  get canMarkComplete() {
    return this.args.instance.canMarkComplete;
  }

  // ── Adapter Method Mapping ─────────────────────────────────────────────

  get adapterMethod() {
    return this._currentAdapterMethod;
  }

  private _currentAdapterMethod: string | undefined = undefined;

  private getAdapterMethodForAction(action: 'approve' | 'reject' | 'requestChange') {
    const actionMap: Record<string, Record<string, string>> = {
      'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL': {
        approve: 'approveProgram',
        reject: 'rejectProgram',
        requestChange: 'requestChanges',
      },
      'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL': {
        approve: 'approveProgramCompletion',
        reject: 'rejectProgramCompletion',
        requestChange: 'requestProgramCompletionChanges',
      },
      'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL': {
        approve: 'approveProgramWithdrawal',
        reject: 'rejectProgramWithdrawal',
        requestChange: 'requestProgramWithdrawalChanges',
      },
    };
    return actionMap[this.state]?.[action];
  }

  // ── Approver Check ─────────────────────────────────────────────────────

  get isExpectedApprover() {
    const expectedApproverEmployeeIds = this.args.instance.currentExpectedApproverEmployeeIds;
    const employees = this.sessionContext.user?.person?.employees || [];
    return doEmployeesExistInApproverList(employees, expectedApproverEmployeeIds);
  }

  // ── Drawer Active State ────────────────────────────────────────────────

  isDrawerActive = (drawerType: ProgramDrawerType) => {
    return this.args.activeDrawer === drawerType;
  };

  handleDrawerClick = (drawerType: ProgramDrawerType) => {
    this.args.onDrawerOpen(drawerType);
  };

  handleMoreActionsMenu = (key: string) => {
    switch (key) {
      case 'approvalQueue':
        this.handleDrawerClick('approval-queue');
        break;
      case 'requestChange':
        this.openRequestChangesModal();
        break;
      case 'reject':
        this.openRejectModal();
        break;
      case 'rescind':
        this.showRescindModal = true;
        break;
    }
  };

  get disabledMenuKeys(): string[] {
    const disabled: string[] = [];
    if (!this.inPendingApprovalState) {
      disabled.push('requestChange');
      disabled.push('reject');
    }
    if (!this.canRescind) {
      disabled.push('rescind');
    }
    return disabled;
  }

  // ── Approval Flow ──────────────────────────────────────────────────────

  openApproveModal = () => {
    this._currentAdapterMethod = this.getAdapterMethodForAction('approve');
    this.approveNote = this.templateApproveComment;
    this.showApproveModal = true;
  };

  closeApproveModal = () => {
    this.showApproveModal = false;
    this.approveNote = '';
    this._currentAdapterMethod = undefined;
    this.hasSubmitted = false;
    this.submitError = null;
  };

  attemptSubmitApproval = () => {
    this.hasSubmitted = true;
    if (!this.approveNote.trim()) return;
    if (!this.isExpectedApprover) {
      this.showConfirmApproverOverridePrompt = true;
    } else {
      this.submitApprovalTask.perform();
    }
  };

  submitApprovalTask = dropTask(async () => {
    this.showConfirmApproverOverridePrompt = false;

    if (!this.adapterMethod || !this.approveNote.trim()) return;

    try {
      this.submitError = null;
      const adapter = this.store.adapterFor('tas-program-instance');
      await adapter[this.adapterMethod](this.args.instance, this.approveNote);
      this.closeApproveModal();
      this.notifications.add(this.intl.t('tuition_assistance.program_details.action_success'), {
        appearance: 'success',
      });
      await this.args.instance.reload();
    } catch (e) {
      console.error(e);
      this.submitError = e instanceof Error ? e.message : String(e);
    }
  });

  // ── More Actions ───────────────────────────────────────────────────────

  openRejectModal = () => {
    this._currentAdapterMethod = this.getAdapterMethodForAction('reject');
    this.rejectNote = this.templateRejectComment;
    this.showRejectModal = true;
  };

  closeRejectModal = () => {
    this.showRejectModal = false;
    this.rejectNote = '';
    this._currentAdapterMethod = undefined;
    this.hasSubmitted = false;
    this.submitError = null;
  };

  openRequestChangesModal = () => {
    this._currentAdapterMethod = this.getAdapterMethodForAction('requestChange');
    this.requestChangeNote = this.templateRequestChangeComment;
    this.showRequestChangesModal = true;
  };

  closeRequestChangesModal = () => {
    this.showRequestChangesModal = false;
    this.requestChangeNote = '';
    this._currentAdapterMethod = undefined;
    this.hasSubmitted = false;
    this.submitError = null;
  };

  submitRejectTask = dropTask(async () => {
    if (!this.adapterMethod || !this.rejectNote.trim()) return;

    try {
      this.submitError = null;
      const adapter = this.store.adapterFor('tas-program-instance');
      await adapter[this.adapterMethod](this.args.instance, this.rejectNote);
      this.closeRejectModal();
      this.notifications.add(this.intl.t('tuition_assistance.program_details.action_success'), {
        appearance: 'success',
      });
      await this.args.instance.reload();
    } catch (e) {
      console.error(e);
      this.submitError = e instanceof Error ? e.message : String(e);
    }
  });

  attemptSubmitReject = () => {
    this.hasSubmitted = true;
    if (!this.rejectNote.trim()) return;
    this.submitRejectTask.perform();
  };

  submitRequestChangesTask = dropTask(async () => {
    if (!this.adapterMethod || !this.requestChangeNote.trim()) return;

    try {
      this.submitError = null;
      const adapter = this.store.adapterFor('tas-program-instance');
      await adapter[this.adapterMethod](this.args.instance, this.requestChangeNote);
      this.closeRequestChangesModal();
      this.notifications.add(this.intl.t('tuition_assistance.program_details.action_success'), {
        appearance: 'success',
      });
      await this.args.instance.reload();
    } catch (e) {
      console.error(e);
      this.submitError = e instanceof Error ? e.message : String(e);
    }
  });

  attemptSubmitRequestChanges = () => {
    this.hasSubmitted = true;
    if (!this.requestChangeNote.trim()) return;
    this.submitRequestChangesTask.perform();
  };

  // ── Rescind ────────────────────────────────────────────────────────────

  rescindTask = dropTask(async () => {
    try {
      const adapter = this.store.adapterFor('tas-program-instance');
      await adapter.rescindLastApproverAction(this.args.instance);
      this.showRescindModal = false;
      await this.args.instance.reload();
    } catch (e) {
      console.error(e);
    }
  });

  // ── Canned Response Notes ──────────────────────────────────────────────

  private getFieldNameForAction(
    action: 'approve' | 'reject' | 'requestChange'
  ): TemplateFieldName | null {
    const fieldNameMap: Record<string, Record<string, TemplateFieldName>> = {
      'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL': {
        approve: 'PROGRAM_APPROVAL_APPROVE_TEXT' as TemplateFieldName,
        reject: 'PROGRAM_APPROVAL_REJECT_TEXT' as TemplateFieldName,
        requestChange: 'PROGRAM_APPROVAL_REVISE_TEXT' as TemplateFieldName,
      },
      'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL': {
        approve: 'WITHDRAW_COMPLETE_APPROVAL_APPROVE_TEXT' as TemplateFieldName,
        reject: 'WITHDRAW_COMPLETE_APPROVAL_REJECT_TEXT' as TemplateFieldName,
        requestChange: 'WITHDRAW_COMPLETE_APPROVAL_REVISE_TEXT' as TemplateFieldName,
      },
      'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL': {
        approve: 'WITHDRAW_COMPLETE_APPROVAL_APPROVE_TEXT' as TemplateFieldName,
        reject: 'WITHDRAW_COMPLETE_APPROVAL_REJECT_TEXT' as TemplateFieldName,
        requestChange: 'WITHDRAW_COMPLETE_APPROVAL_REVISE_TEXT' as TemplateFieldName,
      },
    };
    return fieldNameMap[this.state]?.[action] ?? null;
  }

  private getTagForAction(action: 'approve' | 'reject' | 'requestChange') {
    const tagMap: Record<string, Record<string, string>> = {
      'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL': {
        approve: 'TAS.ProgramApproval.APPROVE',
        reject: 'TAS.ProgramApproval.REJECT',
        requestChange: 'TAS.ProgramApproval.REVISE',
      },
    };
    return tagMap[this.state]?.[action] ?? null;
  }

  private companyCommentsForTag(tag: string): { title: string; body: string }[] {
    const partnerComments = this.args.instance.employee?.company?.comments ?? [];
    return partnerComments
      .filter((comment) => comment.tags?.includes(tag))
      .map((comment) => comment.tasApprovalNote);
  }

  private getResponseOptions(action: 'approve' | 'reject' | 'requestChange') {
    const fieldName = this.getFieldNameForAction(action);
    if (!fieldName) return [];
    const tasFieldOptions = this.args.instance.tasProgramTemplate?.tasFieldOptions ?? [];
    return getSortedOptionsByFieldName(fieldName, tasFieldOptions);
  }

  private getTextAreaOptions(action: 'approve' | 'reject' | 'requestChange') {
    const templateOptions = this.getResponseOptions(action);
    const tag = this.getTagForAction(action);
    const companyOptions = tag ? this.companyCommentsForTag(tag) : [];
    const allOptions = [
      ...templateOptions.map((option) => ({ title: option.label, body: option.value })),
      ...companyOptions,
    ];

    if (allOptions.length > 1) return allOptions;
    return [];
  }

  private getTemplateComment(action: 'approve' | 'reject' | 'requestChange') {
    const templateOptions = this.getResponseOptions(action);
    const tag = this.getTagForAction(action);
    const companyOptions = tag ? this.companyCommentsForTag(tag) : [];
    const allOptions = [
      ...templateOptions.map((o) => ({ title: o.label, body: o.value })),
      ...companyOptions,
    ];

    if (allOptions.length === 1) {
      return allOptions[0]?.body ?? '';
    }

    const fields = this.args.instance.tasProgramTemplate?.fields;
    if (!fields) return '';
    const fieldName = this.getFieldNameForAction(action);
    if (!fieldName) return '';
    const value = getSingleValueForTasField(fieldName, fields);
    return typeof value === 'string' ? value : '';
  }

  get approveTextAreaOptions() {
    return this.getTextAreaOptions('approve');
  }

  get rejectTextAreaOptions() {
    return this.getTextAreaOptions('reject');
  }

  get requestChangeTextAreaOptions() {
    return this.getTextAreaOptions('requestChange');
  }

  get templateApproveComment() {
    return this.getTemplateComment('approve');
  }

  get templateRejectComment() {
    return this.getTemplateComment('reject');
  }

  get templateRequestChangeComment() {
    return this.getTemplateComment('requestChange');
  }

  <template>
    <div
      class="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg rounded-b-lg"
      data-test-program-approval-footer
      ...attributes
    >
      <div class="mx-auto px-2 py-2">
        <div class="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3">
          {{! Primary Approve Button }}
          <Button
            @intent="primary"
            @size="sm"
            @isDisabled={{not this.canApprove}}
            class="md:w-auto w-full"
            {{on "click" this.openApproveModal}}
            data-test-approve-button
          >
            <span class="hidden md:inline">{{t "tuition_assistance.application_details.approve"}}
              {{this.currentPhaseLabel}}</span>
            <span class="md:hidden">{{t "tuition_assistance.application_details.approve"}}</span>
          </Button>

          {{! Icon Buttons Row }}
          <div class="flex items-center justify-around md:justify-end gap-1 md:gap-1">
            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.comments"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "comments"}}
              data-test-button="footer-comments-button"
              {{on "click" (fn this.handleDrawerClick "comments")}}
            >
              <PhChatCenteredText @size="20" class="md:hidden" />
              <PhChatCenteredText @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.partner_information"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "partner-info"}}
              data-test-button="footer-partner-info-button"
              {{on "click" (fn this.handleDrawerClick "partner-info")}}
            >
              <PhBuilding @size="20" class="md:hidden" />
              <PhBuilding @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.attachments"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "attachments"}}
              data-test-button="footer-attachments-button"
              {{on "click" (fn this.handleDrawerClick "attachments")}}
            >
              <PhFile @size="20" class="md:hidden" />
              <PhFile @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.timeline"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "timeline"}}
              data-test-button="footer-timeline-button"
              {{on "click" (fn this.handleDrawerClick "timeline")}}
            >
              <PhCalendarDots @size="20" class="md:hidden" />
              <PhCalendarDots @size="24" class="hidden md:block" />
            </TooltipButton>

            {{! More Actions Popover }}
            <Popover @placement="top-end">
              <:trigger as |pop|>
                <TooltipButton
                  @tooltip={{t "tuition_assistance.application_approval.more_actions"}}
                  @tooltipPlacement="top"
                  @appearance="minimal"
                  @intent="default"
                  @size="sm"
                  class="!p-2 !rounded-md hidden md:flex"
                  {{pop.register}}
                  {{on "click" pop.toggle}}
                >
                  <PhDotsThreeVertical @size="24" />
                </TooltipButton>
              </:trigger>
              <:body>
                <Listbox
                  @intent="primary"
                  @onAction={{this.handleMoreActionsMenu}}
                  @disabledKeys={{this.disabledMenuKeys}}
                  as |l|
                >
                  <l.Item @key="approvalQueue">
                    <span class="flex items-center gap-2">
                      <PhUsers @size="16" />
                      {{t "tuition_assistance.application_approval.approval_queue"}}
                    </span>
                  </l.Item>

                  <div class="border-t border-gray-200 my-1"></div>

                  <l.Item @key="requestChange">
                    <span class="flex items-center gap-2">
                      <PhArrowsCounterClockwise @size="16" />
                      {{t "tuition_assistance.application_details.request_change"}}
                    </span>
                  </l.Item>

                  <l.Item @key="rescind">
                    <span class="flex items-center gap-2">
                      <PhArrowUUpLeft @size="16" />
                      {{t "tuition_assistance.application_approval.undo_last"}}
                    </span>
                  </l.Item>

                  <div class="border-t border-gray-200 my-1"></div>

                  <l.Item @key="reject" @intent="danger">
                    <span class="flex items-center gap-2">
                      <PhXCircle @size="16" />
                      {{t "tuition_assistance.application_details.reject"}}
                    </span>
                  </l.Item>
                </Listbox>
              </:body>
            </Popover>
          </div>
        </div>
      </div>
    </div>

    {{! ── Approve Modal ────────────────────────────────────────────────── }}
    <Modal @isOpen={{this.showApproveModal}} @onClose={{this.closeApproveModal}}>
      <:header>{{t "tuition_assistance.application_details.approve"}}
        {{this.currentPhaseLabel}}</:header>
      <:body>
        <div class="space-y-4">
          <div data-test-approve-note>
            {{#if this.approveTextAreaOptions.length}}
              <SelectTextArea
                @options={{this.approveTextAreaOptions}}
                @optionLabel="title"
                @optionValue="body"
                @value={{this.approveNote}}
                @onChange={{fn (mut this.approveNote)}}
                @errors={{this.approveCommentErrors}}
                @placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
              />
            {{else}}
              <Textarea
                @value={{this.approveNote}}
                @onChange={{fn (mut this.approveNote)}}
                @errors={{this.approveCommentErrors}}
                placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
              />
            {{/if}}
          </div>

          {{#if this.submitError}}
            <p class="text-sm text-red-600">{{this.submitError}}</p>
          {{/if}}
        </div>
      </:body>
      <:footer>
        <div class="flex gap-2 justify-end">
          <Button
            @appearance="outlined"
            data-test-approve-cancel-button
            {{on "click" this.closeApproveModal}}
          >
            {{t "common.cancel"}}
          </Button>
          <Button
            @intent="primary"
            @isRunning={{this.submitApprovalTask.isRunning}}
            data-test-approve-submit-button
            {{on "click" this.attemptSubmitApproval}}
          >
            {{t "tuition_assistance.application_details.approve"}}
          </Button>
        </div>
      </:footer>
    </Modal>

    {{! ── Reject Modal ─────────────────────────────────────────────────── }}
    <Modal @isOpen={{this.showRejectModal}} @onClose={{this.closeRejectModal}}>
      <:header>{{t "tuition_assistance.application_details.reject"}}
        {{this.currentPhaseLabel}}</:header>
      <:body>
        <div class="space-y-4">
          <div data-test-reject-note>
            {{#if this.rejectTextAreaOptions.length}}
              <SelectTextArea
                @options={{this.rejectTextAreaOptions}}
                @optionLabel="title"
                @optionValue="body"
                @value={{this.rejectNote}}
                @onChange={{fn (mut this.rejectNote)}}
                @errors={{this.rejectCommentErrors}}
                @placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
              />
            {{else}}
              <Textarea
                @value={{this.rejectNote}}
                @onChange={{fn (mut this.rejectNote)}}
                @errors={{this.rejectCommentErrors}}
                placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
              />
            {{/if}}
          </div>
          {{#if this.submitError}}
            <p class="text-sm text-red-600">{{this.submitError}}</p>
          {{/if}}
        </div>
      </:body>
      <:footer>
        <div class="flex gap-2 justify-end">
          <Button
            @appearance="outlined"
            data-test-reject-cancel-button
            {{on "click" this.closeRejectModal}}
          >
            {{t "common.cancel"}}
          </Button>
          <Button
            @intent="danger"
            @isRunning={{this.submitRejectTask.isRunning}}
            data-test-reject-submit-button
            {{on "click" this.attemptSubmitReject}}
          >
            {{t "tuition_assistance.application_details.reject"}}
          </Button>
        </div>
      </:footer>
    </Modal>

    {{! ── Request Changes Modal ────────────────────────────────────────── }}
    <Modal @isOpen={{this.showRequestChangesModal}} @onClose={{this.closeRequestChangesModal}}>
      <:header>{{t "tuition_assistance.application_details.request_change"}}</:header>
      <:body>
        <div class="space-y-4">
          <div data-test-request-change-note>
            {{#if this.requestChangeTextAreaOptions.length}}
              <SelectTextArea
                @options={{this.requestChangeTextAreaOptions}}
                @optionLabel="title"
                @optionValue="body"
                @value={{this.requestChangeNote}}
                @onChange={{fn (mut this.requestChangeNote)}}
                @errors={{this.requestChangeCommentErrors}}
                @placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
              />
            {{else}}
              <Textarea
                @value={{this.requestChangeNote}}
                @onChange={{fn (mut this.requestChangeNote)}}
                @errors={{this.requestChangeCommentErrors}}
                placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
              />
            {{/if}}
          </div>
          {{#if this.submitError}}
            <p class="text-sm text-red-600">{{this.submitError}}</p>
          {{/if}}
        </div>
      </:body>
      <:footer>
        <div class="flex gap-2 justify-end">
          <Button
            @appearance="outlined"
            data-test-request-change-cancel-button
            {{on "click" this.closeRequestChangesModal}}
          >
            {{t "common.cancel"}}
          </Button>
          <Button
            @intent="warning"
            @isRunning={{this.submitRequestChangesTask.isRunning}}
            data-test-request-change-submit-button
            {{on "click" this.attemptSubmitRequestChanges}}
          >
            {{t "tuition_assistance.application_details.request_change"}}
          </Button>
        </div>
      </:footer>
    </Modal>

    {{! ── Rescind Confirmation ─────────────────────────────────────────── }}
    <ConfirmationDialog
      @isOpen={{this.showRescindModal}}
      @title={{t "tuition_assistance.application_details.rescind.confirm_button"}}
      @intent="warning"
      @onConfirm={{this.rescindTask.perform}}
      @onCancel={{fn (mut this.showRescindModal) false}}
      @isRunning={{this.rescindTask.isRunning}}
      @confirmText={{t "tuition_assistance.application_details.rescind.confirm_button"}}
      @cancelText={{t "common.cancel"}}
    >
      <p class="mb-2 font-semibold">
        {{t "tuition_assistance.application_details.rescind.body"}}
      </p>
    </ConfirmationDialog>

    {{! ── Approver Override Confirmation ───────────────────────────────── }}
    <ConfirmationDialog
      @isOpen={{this.showConfirmApproverOverridePrompt}}
      @title="Out of Sequence Approver"
      @intent="warning"
      @onConfirm={{this.submitApprovalTask.perform}}
      @onCancel={{fn (mut this.showConfirmApproverOverridePrompt) false}}
      @isRunning={{this.submitApprovalTask.isRunning}}
      @confirmText={{t "common.yes"}}
      @cancelText={{t "common.no"}}
    >
      <p class="mb-2 font-semibold">
        {{t "tuition_assistance.application_details.out_of_sequence_approver_warning"}}
      </p>
    </ConfirmationDialog>
  </template>
}
